"use client";

import { Card, CardContent } from "@workspace/ui/components/card";
import Link from "next/link";
import { GenreBadgeListFromData } from "@/components/shared/genre-badge-list";
import { ArtistPhoto } from "@/components/shared/photos/artist-photo";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";

interface GroupMember {
	artist_id: string;
	artist_name: string;
	artist_slug?: string;
	artist_image_url: string | null;
	artist_spotify_id: string | null;
	artist_spotify_genres: string[] | null;
	artist_manual_genres: string[] | null;
}

interface GroupMembersRowProps {
	members: GroupMember[];
}

export function GroupMembersRow({ members }: GroupMembersRowProps) {
	if (!members || members.length === 0) return null;

	// Determine grid columns based on number of members
	const gridCols =
		members.length === 2 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3";

	return (
		<div className={`grid ${gridCols} gap-3`}>
			{members.map((member) => (
				<Link
					key={member.artist_id}
					href={`/artists/${createArtistSlug(member.artist_name, member.artist_slug)}`}
					className="group"
					style={{ WebkitTapHighlightColor: "transparent" }}
				>
					<Card className="overflow-hidden transition-colors duration-100 hover:bg-muted/30 dark:hover:bg-muted/10 active:bg-muted/50 dark:active:bg-muted/20 active:scale-[0.99] border dark:hover:border-border/60 touch-manipulation">
						<CardContent className="p-3">
							<div className="flex items-start gap-3">
								<div className="flex-shrink-0">
									<ArtistPhoto
										name={member.artist_name}
										imageUrl={member.artist_image_url}
										size="md"
										rounded="card"
										aspectRatio="square"
										className="transition-transform group-hover:scale-105"
									/>
								</div>
								<div className="min-w-0 flex-1">
									<h3 className="font-medium text-sm">{member.artist_name}</h3>
									<div className="mt-1">
										<GenreBadgeListFromData
											spotifyGenres={member.artist_spotify_genres}
											manualGenres={member.artist_manual_genres}
											className="gap-1"
											maxDisplay={2}
											badgeClassName="text-xs"
										/>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>
				</Link>
			))}
		</div>
	);
}
